import React, { useState, useEffect,useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import {
  Eye,
  FileText,
  Calendar,
  User,
  Car,
  Search,
  Download
} from "lucide-react";
import "./AdminInspectionReports.css";

const AdminInspectionReports = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const userId = localStorage.getItem("userId");

  /* ================= STATE ================= */

  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [dense, setDense] = useState(false);

  const [fleetExporting, setFleetExporting] = useState(false);

  const [filters, setFilters] = useState({
    search: "",
    status: "all",
    startDate: "",
    endDate: ""
  });

    /* ================= GUARDS ================= */

  // Single abort controller ref for the entire component lifecycle
  const abortRef = useRef(null);

  // prevents multiple parallel API calls
  const fetchingRef = useRef(false);

  // track if initial load has happened
  const initialLoadDone = useRef(false);

  /* ================= API ================= */

  // Helper: cancel any in-flight requests
  const cancelPendingRequests = () => {
    if (abortRef.current) {
      abortRef.current.abort();
    }
    abortRef.current = new AbortController();
    fetchingRef.current = false;
    return abortRef.current.signal;
  };

  const loadAll = async (reset = true) => {
    const signal = cancelPendingRequests();
    setLoading(true);

    try {
      // Fetch stats and reports in parallel (only 2 requests)
      const [statsRes, reportsRes] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/general-inspection/stats`,
          {
            params: filters,
            headers: { Authorization: `Bearer ${token}` },
            signal
          }
        ),
        axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/general-inspection/reports`,
          {
            params: {
              ...filters,
              cursor: reset ? null : cursor,
              limit: 20
            },
            headers: { Authorization: `Bearer ${token}` },
            signal
          }
        )
      ]);

      setStats(statsRes.data.data);

      const newData = reportsRes.data.data || [];
      setReports(prev => (reset ? newData : [...prev, ...newData]));
      setCursor(reportsRes.data.nextCursor);
      setHasMore(reportsRes.data.hasMore);
    } catch (err) {
      if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
        console.error('Error fetching inspection data:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadMoreReports = async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    setLoading(true);

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/general-inspection/reports`,
        {
          params: { ...filters, cursor, limit: 20 },
          headers: { Authorization: `Bearer ${token}` },
          signal: abortRef.current?.signal
        }
      );
      const newData = res.data.data || [];
      setReports(prev => [...prev, ...newData]);
      setCursor(res.data.nextCursor);
      setHasMore(res.data.hasMore);
    } catch (err) {
      if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
        console.error('Error loading more reports:', err);
      }
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  };

  /* ================= INITIAL LOAD ================= */

  useEffect(() => {
    if (!token || !userId) {
      navigate('/login');
      return;
    }

    initialLoadDone.current = false;
    loadAll(true).then(() => {
      initialLoadDone.current = true;
    });

    return () => {
      // Cancel all in-flight requests on unmount
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  /* ================= FILTER CHANGE (DEBOUNCE) ================= */

  useEffect(() => {
    // Skip the very first render (initial load already handles it)
    if (!initialLoadDone.current) return;

    const delay = setTimeout(() => {
      setCursor(null);
      loadAll(true);
    }, 400);

    return () => clearTimeout(delay);
  }, [filters]);

  /* ================= HELPERS ================= */

  const getStatusColor = status => {
    switch (status?.toLowerCase()) {
      case "submitted":
        return "status-submitted";
      case "reviewed":
        return "status-reviewed";
      case "approved":
        return "status-approved";
      case "rejected":
        return "status-rejected";
      default:
        return "status-default";
    }
  };

  const handleViewReport = inspectionCode => {
    navigate(`/inspection-report/${inspectionCode}`);
  };

const exportToExcel = async () => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/api/general-inspection/reports/export-excel`,
      {
        params: filters,
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      }
    );

    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `InspectionReports.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (err) {
    console.error("Excel download failed", err);
    alert("Failed to export Excel");
  }
};

// Export Fleet excel
const exportFleetList = async () => {
  try {
    setFleetExporting(true);

    const res = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/api/general-inspection/reports/export-ulip-compliance`,
      {
        params: {
          ...(filters.startDate && { startDate: filters.startDate }),
          ...(filters.endDate   && { endDate:   filters.endDate   }),
        },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      }
    );

    const url  = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href  = url;
    link.setAttribute('download', `fleet_list_${new Date().toISOString().split('T')[0]}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

  } catch (err) {
    console.error('Fleet list export failed:', err);
    alert('Failed to export Fleet List');
  } finally {
    setFleetExporting(false);
  }
};


  /* ================= UI ================= */

  return (
    <div className="admin-inspection-container">
      <div className="admin-inspection-content">
        {/* HEADER */}
        <div className="inspection-header">
          <div className="header-title">
            <FileText size={30} className="title-icon" />
            <div>
              <h1>Vehicle Inspection Reports</h1>
              <p>Manage and review vehicle inspection reports</p>
            </div>
          </div>

          <div className="header-actions">
            <button
              className="density-btn"
              onClick={() => setDense((prev) => !prev)}
            >
              {dense ? "Comfortable" : "Compact"}
            </button>

            <button
              className="export-btn"
              onClick={exportFleetList}
              disabled={fleetExporting}
              style={{ opacity: fleetExporting ? 0.6 : 1, cursor: fleetExporting ? 'not-allowed' : 'pointer' }}
            >
              <Download size={18} />
              {fleetExporting ? 'Exporting...' : 'Fleet List'}
            </button>

            <button className="export-btn" onClick={exportToExcel}>
              <Download size={18} />
              Inspection
            </button>
          </div>
        </div>

        {/* TOOLBAR */}
        <div className="inspection-toolbar">
          <div className="toolbar-left">
            <div className="search-field">
              <Search size={16} />
              <input
                type="text"
                placeholder="Search vehicle / client / code"
                value={filters.search}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, search: e.target.value }))
                }
              />
            </div>

            <select
              className="toolbar-select"
              value={filters.status}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, status: e.target.value }))
              }
            >
              <option value="all">All Status</option>
              <option value="submitted">Submitted</option>
              <option value="reviewed">Reviewed</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>

            <div className="date-field">
              <Calendar size={14} />
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, startDate: e.target.value }))
                }
              />
            </div>

            <span className="date-separator">to</span>

            <div className="date-field">
              <Calendar size={14} />
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, endDate: e.target.value }))
                }
              />
            </div>
          </div>

          {/* <div className="toolbar-actions">
            <button className="icon-btn search-btn">
              <Search size={18} />
            </button>

            <button className="icon-btn export-btn-2" onClick={exportToCSV}>
              <Download size={18} />
            </button>
          </div>*/}
        </div> 

        {/* STATS */}
        <div className="inspection-stats">
          <div className="stat-card">
            <div className="stat-value">{stats?.totalReports ?? 0}</div>
            <div className="stat-label">Total Reports</div>
          </div>

          <div className="stat-card">
            <div className="stat-value">{stats?.submitted ?? 0}</div>
            <div className="stat-label">Pending Review</div>
          </div>

          <div className="stat-card">
            <div className="stat-value">{stats?.approved ?? 0}</div>
            <div className="stat-label">Approved</div>
          </div>

          <div className="stat-card">
            <div className="stat-value">{reports.length}</div>
            <div className="stat-label">Loaded Results</div>
          </div>
        </div>

        {/* TABLE */}
        <div className={`table-container ${dense ? "dense" : ""}`}>
          <table className="inspection-table">
            <thead>
              <tr>
                <th>Inspection Code</th>
                <th>Date</th>
                <th>Client Name</th>
                <th>Driver Name</th>
                <th>Vehicle Details</th>
                <th>Submitted By</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {reports.map((inspection) => (
                <tr key={inspection.inspectionCode}>
                  <td>
                    <div className="inspection-code">
                      <FileText size={16} />
                      {inspection.inspectionCode}
                    </div>
                  </td>

                  <td>
                    <div className="date-cell">
                      <Calendar size={14} />
                      {inspection.inspectionDate
                        ? new Date(
                            inspection.inspectionDate,
                          ).toLocaleDateString()
                        : "N/A"}
                    </div>
                  </td>

                  <td>
                    <div className="name-cell">
                      <User size={14} />
                      {inspection.clientName || "N/A"}
                    </div>
                  </td>

                  <td>{inspection.driverName || "N/A"}</td>

                  <td>
                    <div className="vehicle-cell">
                      <Car size={14} />
                      <div>
                        <div className="vehicle-no">{inspection.vehicleNo}</div>
                        <div className="vehicle-model">
                          {inspection.vehicleModel} - {inspection.vehicleType}
                        </div>
                      </div>
                    </div>
                  </td>

                  <td>
                    <span className="submitted-by-badge">
                      {inspection.submittedByType === "employee"
                        ? "👤 Employee"
                        : "👥 Customer"}
                    </span>
                  </td>

                  <td>
                    <span
                      className={`status-badge ${getStatusColor(inspection.status)}`}
                    >
                      {inspection.status}
                    </span>
                  </td>

                  <td>
                    <button
                      className="view-btn"
                      onClick={() =>
                        handleViewReport(inspection.inspectionCode)
                      }
                    >
                      <Eye size={16} /> View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* LOAD MORE */}
        {hasMore && (
          <div className="load-more">
            <button onClick={loadMoreReports} disabled={loading}>
              {loading ? "Loading..." : "Load More"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminInspectionReports;
